import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Button, Group, Text, SimpleGrid } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { RangePicker, rangeToSinceIso, type RangeOption } from '../../audit/components/RangePicker';
import { DeliveriesTable } from './components/DeliveriesTable';
import { DeliveriesList } from './components/DeliveriesList';
import { DeliveryDrawer } from './components/DeliveryDrawer';
import { outcomeOf, type Delivery, type DeliveryOutcome } from './components/deliveryState';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { CopyableId } from '../../../components/CopyableId';
import { StatCard } from '../../../components/StatCard';
import { TableFooter } from '../../../components/TableFooter';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { useTenantMap } from '../../../data/useTenants';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatInteger, formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
const ROW_LIMIT = 500;
const SLOW_MS = 1000;

type KpiKey = 'all' | DeliveryOutcome | 'slow';

const SendIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20.5 3.5L11 13M20.5 3.5l-6.5 17-3-7.5-7.5-3z" />
  </svg>
);
const CheckIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4.5 12.5l5 5 10-11" />
  </svg>
);
const CrossIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
    <path d="M7 7l10 10M17 7L7 17" />
  </svg>
);
const ShieldIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3l7 3v5.5c0 4.4-3 7.9-7 9.5-4-1.6-7-5.1-7-9.5V6z" />
  </svg>
);

export default function WebhookDeliveriesPage() {
  const t = useT();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<RangeOption>('24h');
  const since = useMemo(() => rangeToSinceIso(range), [range]);

  // `/webhooks` links here for one endpoint, so the subscription filter lives
  // in the URL and the link is the whole workflow.
  const subscriptionId = params.get('subscription_id') ?? '';

  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'audit', 'deliveries', since],
    queryFn: () => admin.listDeliveries({ since })
  });

  /*
   * The delivery row names its subscription by id only, and an id is a join
   * key rather than something to read. The subscriptions list is the same
   * cached query the /webhooks page uses, so resolving the endpoint URL here
   * costs one cache read and turns a GUID column into an addressable endpoint.
   */
  const subsQ = useQuery({
    queryKey: ['admin', 'webhooks', 'subscriptions'],
    queryFn: () => admin.listWebhookSubscriptions({ limit: ROW_LIMIT })
  });
  const endpointById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subsQ.data?.data ?? []) m.set(s.id, s.url);
    return m;
  }, [subsQ.data]);

  const all = useMemo(() => data?.data ?? [], [data]);
  const { map: tenants } = useTenantMap();
  const denied = isApiError(error) && error.status === 403;
  const capped = all.length >= ROW_LIMIT;
  const failed = Boolean(error);

  const [view, setView] = usePersistedState<ViewMode>('admin.webhook-deliveries.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.webhook-deliveries.pageSize', 25);

  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<Delivery | null>(null);

  const counts = useMemo(() => ({
    all: all.length,
    success: all.filter((d) => outcomeOf(d) === 'success').length,
    failed: all.filter((d) => outcomeOf(d) === 'failed').length,
    blocked: all.filter((d) => outcomeOf(d) === 'blocked').length,
    pending: all.filter((d) => outcomeOf(d) === 'pending').length,
    slow: all.filter((d) => (d.latency_ms ?? 0) > SLOW_MS).length
  }), [all]);

  const share = (n: number) => (counts.all === 0 ? '0.0' : ((n / counts.all) * 100).toFixed(1));

  const kpiItems: KpiFilterItem[] = [
    { id: 'all',     label: t('webhooks.deliveries.kpi.all'),     value: counts.all,     tone: 'primary' },
    { id: 'success', label: t('webhooks.deliveries.kpi.success'), value: counts.success, tone: 'healthy' },
    { id: 'failed',  label: t('status.failed'),                   value: counts.failed,  tone: 'down' },
    { id: 'blocked', label: t('webhooks.deliveries.blocked'),     value: counts.blocked, tone: 'warning' },
    { id: 'pending', label: t('status.pending'),                  value: counts.pending, tone: 'pending' },
    { id: 'slow',    label: t('webhooks.deliveries.kpi.slow'),    value: counts.slow,    tone: 'warning' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    const events = Array.from(new Set(all.map((d) => d.event_type))).sort();
    const orgs = Array.from(new Set(all.map((d) => d.organisation_id)))
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: 'event_type',
        label: t('webhooks.deliveries.filters.eventType'),
        placeholder: t('webhooks.deliveries.filters.eventTypePlaceholder'),
        options: events.map((e) => ({ value: e, label: e }))
      },
      {
        key: 'organisation',
        label: t('webhooks.filters.organisation'),
        placeholder: t('webhooks.filters.organisationPlaceholder'),
        options: orgs
      },
      {
        key: 'status_class',
        label: t('webhooks.deliveries.filters.statusClass'),
        placeholder: t('webhooks.deliveries.filters.statusClassPlaceholder'),
        options: [
          { value: '2xx', label: t('webhooks.deliveries.filters.statusClass.success') },
          { value: '3xx', label: t('webhooks.deliveries.filters.statusClass.redirect') },
          { value: '4xx', label: t('webhooks.deliveries.filters.statusClass.clientError') },
          { value: '5xx', label: t('webhooks.deliveries.filters.statusClass.serverError') },
          { value: 'blocked', label: t('webhooks.deliveries.blocked') },
          { value: 'pending', label: t('status.pending') }
        ]
      }
    ];
  }, [all, tenants, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    (kpi !== null && kpi !== 'all') || drawerActive > 0 || range !== '24h' ||
    Boolean(query.trim()) || Boolean(subscriptionId);

  function clearAllFilters() {
    setKpi('all'); setFilters({}); setRange('24h'); setQuery(''); setPage(1);
    setParams(new URLSearchParams(), { replace: true });
  }

  const statusClassLabel: Record<string, string> = {
    '2xx': t('webhooks.deliveries.filters.statusClass.success'),
    '3xx': t('webhooks.deliveries.filters.statusClass.redirect'),
    '4xx': t('webhooks.deliveries.filters.statusClass.clientError'),
    '5xx': t('webhooks.deliveries.filters.statusClass.serverError'),
    blocked: t('webhooks.deliveries.blocked'),
    pending: t('status.pending')
  };

  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (range !== '24h') pills.push({ id: 'range', field: t('audit.range.label'), value: t(`audit.range.${range}`) });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('common.status'), value: kpiItems.find((k) => k.id === kpi)?.label ?? kpi });
  }
  if (subscriptionId) {
    pills.push({
      id: 'subscription_id',
      field: t('webhooks.deliveries.detail.subscription'),
      value: endpointById.get(subscriptionId) ?? t('webhooks.deliveries.unknownEndpoint')
    });
  }
  if (filters.event_type) pills.push({ id: 'event_type', field: t('webhooks.deliveries.filters.eventType'), value: filters.event_type });
  if (filters.organisation) {
    pills.push({
      id: 'organisation',
      field: t('webhooks.filters.organisation'),
      value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort')
    });
  }
  if (filters.status_class) {
    pills.push({ id: 'status_class', field: t('webhooks.deliveries.filters.statusClass'), value: statusClassLabel[filters.status_class] ?? filters.status_class });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') { setQuery(''); return; }
    if (id === 'range') { setRange('24h'); return; }
    if (id === 'kpi') { setKpi('all'); return; }
    if (id === 'subscription_id') { setParams(new URLSearchParams(), { replace: true }); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  const filtered = useMemo(() => {
    let rows = all;
    if (subscriptionId) rows = rows.filter((d) => d.subscription_id === subscriptionId);
    if (kpi && kpi !== 'all') {
      rows = kpi === 'slow'
        ? rows.filter((d) => (d.latency_ms ?? 0) > SLOW_MS)
        : rows.filter((d) => outcomeOf(d) === kpi);
    }
    if (filters.event_type) rows = rows.filter((d) => d.event_type === filters.event_type);
    if (filters.organisation) rows = rows.filter((d) => d.organisation_id === filters.organisation);
    if (filters.status_class) {
      const sc = filters.status_class;
      rows = rows.filter((d) => {
        const outcome = outcomeOf(d);
        if (sc === 'blocked') return outcome === 'blocked';
        if (sc === 'pending') return outcome === 'pending';
        if (outcome === 'blocked' || d.http_status == null) return false;
        const s = d.http_status;
        if (sc === '2xx') return s >= 200 && s < 300;
        if (sc === '3xx') return s >= 300 && s < 400;
        if (sc === '4xx') return s >= 400 && s < 500;
        if (sc === '5xx') return s >= 500;
        return true;
      });
    }
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((d) => {
        const tenant = tenants.get(d.organisation_id);
        const endpoint = endpointById.get(d.subscription_id) ?? '';
        return `${d.event_type} ${d.id} ${endpoint} ${tenant?.name ?? ''}`.toLowerCase().includes(q);
      });
    }
    return rows;
  }, [all, kpi, filters, query, tenants, subscriptionId, endpointById]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('webhooks.title')}
        title={t('webhooks.deliveries.title')}
        description={t('webhooks.deliveries.description')}
        actions={
          <Group gap="sm" wrap="wrap">
            {dataUpdatedAt > 0 && (
              <Text role="caption" c={MUTED}>
                {t('webhooks.lastUpdated', { when: formatRelative(new Date(dataUpdatedAt).toISOString()) })}
              </Text>
            )}
            <Button size="sm" variant="default" onClick={() => refetch()} loading={isFetching}>{t('action.refresh')}</Button>
            {/* The window is the first thing an operator changes on this page,
                so it sits with the page's own controls rather than inside the
                table toolbar where it read as one filter among several. */}
            <RangePicker value={range} onChange={(r) => { setRange(r); setPage(1); }} />
            <Button size="sm" variant="subtle" onClick={() => nav('/webhooks')}>{t('webhooks.deliveries.openSubscriptions')}</Button>
          </Group>
        }
      />

      {denied && (
        <Alert color="warning" title={t('webhooks.denied.title')}>{t('webhooks.denied.body')}</Alert>
      )}

      {subscriptionId && (
        <Alert color="info">
          <Group justify="space-between" align="center" style={{ width: '100%' }} wrap="wrap">
            <Group gap="xs" align="center">
              <span>{t('webhooks.deliveries.scopedToSubscription')}</span>
              <CopyableId value={subscriptionId} label={t('webhooks.deliveries.copySubscriptionId')} />
            </Group>
            <Button size="xs" variant="outline" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              {t('webhooks.deliveries.showAllSubscriptions')}
            </Button>
          </Group>
        </Alert>
      )}

      {counts.blocked > 0 && !failed && (
        <Alert color="warning">{t('webhooks.deliveries.blockedNotice', { count: counts.blocked })}</Alert>
      )}

      {/* Four headline figures, not six: the chips below already carry every
          outcome as a filter, so the cards answer the questions the chips do
          not, which are the rates rather than the counts. A failed query says
          "n/a", never a zero that reads as a quiet platform. */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          icon={SendIcon}
          tone="primary"
          label={t('webhooks.deliveries.kpi.all')}
          value={failed ? t('common.notAvailable') : formatInteger(counts.all)}
          hint={failed ? undefined : t(`audit.range.${range}`)}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={CheckIcon}
          tone="success"
          label={t('webhooks.deliveries.stats.successRate')}
          value={failed ? t('common.notAvailable') : `${share(counts.success)}%`}
          hint={failed ? undefined : t('webhooks.deliveries.stats.ofAttempts', { count: formatInteger(counts.success), total: formatInteger(counts.all) })}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={CrossIcon}
          tone="error"
          label={t('status.failed')}
          value={failed ? t('common.notAvailable') : formatInteger(counts.failed)}
          hint={failed ? undefined : t('webhooks.deliveries.stats.failedHint')}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={ShieldIcon}
          tone="warning"
          label={t('webhooks.deliveries.blocked')}
          value={failed ? t('common.notAvailable') : formatInteger(counts.blocked)}
          hint={failed ? undefined : t('webhooks.deliveries.stats.blockedHint')}
          loading={isLoading}
          withRule
        />
      </SimpleGrid>

      <TableShell
        title={t('webhooks.deliveries.tableTitle')}
        totalLabel={
          capped
            ? t('webhooks.deliveries.totalLabelCapped', { shown: filtered.length, total: all.length, range: t(`audit.range.${range}`) })
            : t('webhooks.deliveries.totalLabel', { shown: filtered.length, total: all.length, range: t(`audit.range.${range}`) })
        }
        description={capped ? t('webhooks.deliveries.maxRows', { limit: ROW_LIMIT }) : undefined}
        kpis={<KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />}
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('webhooks.deliveries.searchPlaceholder')}
        filters={
          <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
        }
        view={view}
        onViewChange={setView}
        availableViews={['table', 'list']}
        loading={isLoading}
        isEmpty={!isLoading && !error && filtered.length === 0}
        empty={
          <EmptyState
            title={t('webhooks.deliveries.empty')}
            description={hasActiveFilters ? t('webhooks.deliveries.emptyFiltered') : t('webhooks.deliveries.emptyHint')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        error={error && !denied ? (
          <Alert color="error">
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <span>{t('webhooks.deliveries.loadError')}</span>
              <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            capped={capped}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        }
      >
        <Stack gap="md">
          <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

          {view === 'table' && (
            <DeliveriesTable
              rows={paged}
              loading={isLoading}
              endpointFor={(id) => endpointById.get(id)}
              onRowClick={setDetail}
            />
          )}
          {view === 'list' && <DeliveriesList records={paged} onSelect={setDetail} />}
        </Stack>
      </TableShell>

      <DeliveryDrawer
        row={detail}
        endpointUrl={detail ? endpointById.get(detail.subscription_id) : undefined}
        onClose={() => setDetail(null)}
      />
    </Stack>
  );
}
